import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import moment from 'moment'
import CSV from 'papaparse'

import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Calendar } from 'primereact/calendar';
import Isvg from 'react-inlinesvg'

import auditLogService, { AuditLog_t } from 'data/audit-log'
import codeService from 'data/codes'
import propertyService from 'data/property'
import { getParser } from './controls/change-renderer'
import { local } from 'data/lib/local-json'

import { AuditLogTable_s } from './audit-log-styles'
import { ChangeRenderer } from './controls/change-renderer'
import { DownloadIcon } from 'images/icons/tools'

// fetch errors for
const resources = [
  'area',
  'downtime',
  'freerent',
  'increase',
  'inflation',
  'lineitem',
  'marketlease',
  'multifamily',
  'payout',
  'property',
  'recovery',
  'recoverygroup',
  'recoverypool',
  'renewalprobability',
  'rent',
  'rentroll',
  'reporttag',
  'room',
  'tenantcapital',
  'tenantcontribution',
  'tenantgroup'
]

// get codes for
const dependencies = [
  'folder',
  'area',
  'downtime',
  'freerent',
  'increase',
  'inflation',
  'lineitem',
  'marketlease',
  'multifamily',
  'payout',
  'property',
  'recovery',
  'recoverygroup',
  'recoverypool',
  'renewalprobability',
  'rent',
  'rentroll',
  'reporttag',
  'room',
  'tenantcapital',
  'tenantcontribution',
  'tenantgroup'
]

type Filters = {
  change?: string
  resource?: string
  resourcecode?: string
  'user.email'?: string
  created_start?: Date
  created_end?: Date
}

export const PropertyAuditLog = (props) => {
  const [propertyid, setPropertyid] = useState<number>(local('property'))
  const [data, setData] = useState<AuditLog_t[]>([])
  const [filter, setFilter] = useState<Filters>({})
  const [_codes, setCodes] = useState<any>()
  useEffect(() => {
    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.current !== null && data.current !== propertyid) setPropertyid(data.current)
    })

    const auditLogSubscription = auditLogService.stream().subscribe((data) => {
      setData(data.value)
    })

    const codeSubscription = codeService.stream().subscribe(setCodes);

    // fetched here so children can just sub/get the stream value without repeatedly fetching
    codeService.fetch(dependencies, propertyid)

    auditLogService.fetch(resources, [`propertyid=eq.${propertyid}`])

    return () => {
      auditLogSubscription.unsubscribe()
      codeSubscription.unsubscribe()
      propertySubscription.unsubscribe()
    }
  }, [propertyid])

  useEffect(() => {
    let query: string[] = []
    let qresources = [...resources]
    for (let field in filter) {
      const fval = filter[field]
      if (_.size(fval) < 3 && !_.isDate(fval)) continue
      switch (field) {
        case 'resource':
          qresources = []
          query.push(`resource=like.${fval}*`)
          break
        case 'resourcecode':
          query.push(`resourcecode=ilike.${fval}*`)
          break
        case 'change':
          if (fval == 'Copied') {
            query.push(`correlationid=isnot.null`)
            break
          }
          query.push(`correlationid=is.null`)
          query.push(`change=ilike.*${fval}*`)
          break
        case 'created_start':
          query.push(`created=gte.${moment(fval).format('YYYY-MM-DDTHH:mm:ss-07:00')}`)
          break
        case 'created_end':
          query.push(`created=lte.${moment(fval).format('YYYY-MM-DDTHH:mm:ss-07:00')}`)
          break
        case 'user.email':
          query.push(`user->>email=ilike.*${fval}*`)
          break
        default:
          query.push(`${field}=ilike.*${encodeURIComponent(fval)}*`)
      }
    }
    query.push(`propertyid=eq.${propertyid}`)
    if (fetchDebounce) clearTimeout(fetchDebounce)
    setFetchDebounce(setTimeout(() => auditLogService.fetch(qresources, query), 250))
  }, [filter])

  const [fetchDebounce, setFetchDebounce] = useState<number|undefined>()

  const changevalue = (field) => (value) => {
    setFilter({...filter, [field]: value})
  }

  const change = (field) => (event) => changevalue(field)(_.get(event, 'target.value') || _.get(event, 'value'))

  const header = (label, field) =>
    <div>
      {<div className="p-column-title">{label}</div>}
      <input value={filter[field]} onChange={change(field)} className="p-inputtext p-component p-column-filter"/>
    </div>

  const dateheader = (field) =>
     <div className="p-column-filter-date-group">
        <span>
          <div className="p-column-title">Date Min</div>
          <Calendar value={filter['created_start']} onChange={change('created_start')} showTime hourFormat="12" showIcon />
        </span>
        <span>
          <div className="p-column-title">Date Max</div>
          <Calendar value={filter['created_end']} onChange={change('created_end')} showTime hourFormat="12" showIcon />
        </span>
      </div>

  return (
    <AuditLogTable_s id="audit-log-scrollable-container">
      <DataTable
        id="audit-log"
        value={data}
        paginator={true}
        rows={20}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        scrollable={true}
        paginatorPosition="bottom"
        stateStorage="custom"
        customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
        customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
      >
        <Column style={{minWidth: "250px", maxWidth: "250px"}}
          field="resource"
          header={header('Location', 'resource')}
        />
        <Column style={{minWidth: "250px", maxWidth: "250px"}}
          field="resourcecode"
          header={header('Code/Suite', 'resourcecode')}
        />
        <Column style={{minWidth: "250px", maxWidth: "250px"}}
          header={header('User', 'user.email')}
          field="user.email"
        />
        <Column style={{minWidth: "390px", maxWidth: "390px"}}
          header={header('Operation/Change','change')}
          field="change"
          body={ChangeRenderer}
        />
        <Column style={{minWidth: "450px", maxWidth: "450px"}}
          header={dateheader('created')}
          field="created"
          body={(props) => moment(new Date(props['created'])).format('YYYY-MM-DD hh:mm A')}
        />
        <Column style={{minWidth: "25px", maxWidth: "25px"}} header={<Download data={data}/>}/>
      </DataTable>
    </AuditLogTable_s>
  )
}

const Download = ({data}) => {
  const filename = 'Folder-Log'
  const download = async () => {
    const fields = {
      change: 'Change',
      created: 'Change Date',
      method: 'Change Type',
      resource: 'Location',
      user: 'User'
    }
    const methods = {
      POST: 'Create',
      PATCH: 'Update',
      DELETE: 'Delete'
    }
    data = _.cloneDeep(data)
    data = _.map(data, d => getParser(_.pick(d, ['change', 'created', 'resource', 'resourcecode', 'method', 'user']))())
    data.forEach(d => d.user = d.user.email)
    data.forEach(d => d.method = methods[d.method])
    data.forEach(d => {
      d.change = `${d.atRest}\n${_.map(d.diffs, d => `${
        d.added ? '+ ' : d.removed ? '- ' : ''
      }${
        '  '.repeat(d.indent)
      }${d.key}${'value' in d ? `:${d.value}` : ''}`).join('\n')}`
    })

    const keys = ['resource', 'resourcecode', 'user', 'created', 'method', 'change']
    const headers = _.map(keys, (k) => fields[k] || k)
    const rows = _.map(data, d => _.map(keys, k => d[k]))
    const csv = CSV.unparse([headers].concat(rows))

    const element = document.createElement('a')
    element.setAttribute(
      'href',
      'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    )
    element.setAttribute('download', filename + '.csv')
    element.style.display = 'none'
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <span className="log-download" onClick={download}>
      <Isvg src={DownloadIcon}/>
    </span>
  )
}
